import { type FormEvent, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { ConflictBanner } from "@/components/ConflictBanner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth";
import { useRealtime } from "@/context/realtime";
import { useFetch } from "@/hooks/useFetch";

type Project = {
	id: string;
	name: string;
	description: string | null;
	status: string;
	createdBy: string;
	version: number;
	createdAt: string;
	updatedAt: string;
};

type ProjectsListResponse = {
	projects: Project[];
};

export function ProjectsPage() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const { hasPermission } = useAuth();
	const { data, loading, error, refetch } = useFetch<ProjectsListResponse>(
		`/api/tenant/${workspaceSlug}/projects`,
	);
	const { messages } = useRealtime();
	const [conflict, setConflict] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (messages.length === 0) return;
		const latest = messages[messages.length - 1];
		if (!latest.type.startsWith("project.")) return;

		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			setConflict(false);
			refetch();
		}, 200);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [messages, refetch]);

	const [createOpen, setCreateOpen] = useState(false);
	const [createName, setCreateName] = useState("");
	const [createDescription, setCreateDescription] = useState("");
	const [createLoading, setCreateLoading] = useState(false);
	const [createError, setCreateError] = useState("");

	const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteError, setDeleteError] = useState("");

	async function handleCreate(e: FormEvent) {
		e.preventDefault();
		setCreateError("");

		const trimmed = createName.trim();
		if (!trimmed) {
			setCreateError("Name is required");
			return;
		}

		setCreateLoading(true);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/projects`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({
					name: trimmed,
					description: createDescription.trim() || undefined,
				}),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setCreateError(
					(body as { error?: string } | null)?.error ??
						`Create failed (${res.status})`,
				);
				return;
			}
			setCreateName("");
			setCreateDescription("");
			setCreateOpen(false);
			refetch();
		} catch {
			setCreateError("Network error");
		} finally {
			setCreateLoading(false);
		}
	}

	async function handleDelete() {
		if (!deleteTarget) return;
		setDeleteError("");
		setDeleteLoading(true);
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/projects/${deleteTarget.id}`,
				{
					method: "DELETE",
					credentials: "include",
					headers: {
						"Content-Type": "application/json",
						Origin: window.location.origin,
					},
					body: JSON.stringify({ version: deleteTarget.version }),
				},
			);
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				if (
					res.status === 409 &&
					(body as { code?: string } | null)?.code === "CONFLICT_VERSION"
				) {
					setConflict(true);
					setDeleteTarget(null);
					return;
				}
				setDeleteError(
					(body as { error?: string } | null)?.error ??
						`Delete failed (${res.status})`,
				);
				return;
			}
			setDeleteTarget(null);
			refetch();
		} catch {
			setDeleteError("Network error");
		} finally {
			setDeleteLoading(false);
		}
	}

	const projects = data?.projects ?? [];

	function handleConflictRefresh() {
		setConflict(false);
		refetch();
	}

	return (
		<div className="max-w-2xl mx-auto space-y-6">
			{conflict && <ConflictBanner onRefresh={handleConflictRefresh} />}
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-foreground">Projects</h1>
				{hasPermission("projects", "create") && (
					<Dialog
						open={createOpen}
						onOpenChange={(open) => {
							setCreateOpen(open);
							if (!open) {
								setCreateName("");
								setCreateDescription("");
								setCreateError("");
							}
						}}
					>
						<DialogTrigger asChild>
							<Button>New Project</Button>
						</DialogTrigger>
						<DialogContent>
							<form onSubmit={handleCreate}>
								<DialogHeader>
									<DialogTitle>New Project</DialogTitle>
									<DialogDescription>
										Create a new project for your organization.
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-4 py-4">
									<div className="space-y-2">
										<Label htmlFor="project-name">Name</Label>
										<Input
											id="project-name"
											value={createName}
											onChange={(e) => {
												setCreateName(e.target.value);
												setCreateError("");
											}}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="project-description">
											Description (optional)
										</Label>
										<Input
											id="project-description"
											value={createDescription}
											onChange={(e) => setCreateDescription(e.target.value)}
										/>
									</div>
									{createError && (
										<p className="text-sm text-destructive">{createError}</p>
									)}
								</div>
								<DialogFooter>
									<Button type="submit" disabled={createLoading}>
										{createLoading ? "Creating\u2026" : "Create"}
									</Button>
								</DialogFooter>
							</form>
						</DialogContent>
					</Dialog>
				)}
			</div>

			{error && <p className="text-sm text-destructive">{error}</p>}

			{loading && !data && (
				<p className="text-sm text-muted-foreground">Loading\u2026</p>
			)}

			{data && projects.length === 0 && (
				<p className="text-sm text-muted-foreground">No projects yet.</p>
			)}

			{projects.map((project) => (
				<Card key={project.id}>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle className="text-lg">{project.name}</CardTitle>
								{project.description && (
									<CardDescription>{project.description}</CardDescription>
								)}
							</div>
							{hasPermission("projects", "delete") && (
								<Button
									variant="destructive"
									size="sm"
									onClick={() => setDeleteTarget(project)}
								>
									Delete
								</Button>
							)}
						</div>
					</CardHeader>
					<CardContent>
						<div className="flex items-center gap-4 text-sm text-muted-foreground">
							<span>Status: {project.status}</span>
							<span>
								Created: {new Date(project.createdAt).toLocaleDateString()}
							</span>
						</div>
					</CardContent>
				</Card>
			))}

			<Dialog
				open={deleteTarget !== null}
				onOpenChange={(open) => {
					if (!open) {
						setDeleteTarget(null);
						setDeleteError("");
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete Project</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-semibold">{deleteTarget?.name}</span>? This
							action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					{deleteError && (
						<p className="text-sm text-destructive">{deleteError}</p>
					)}
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteTarget(null)}>
							Cancel
						</Button>
						<Button
							variant="destructive"
							disabled={deleteLoading}
							onClick={handleDelete}
						>
							{deleteLoading ? "Deleting\u2026" : "Delete"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
